import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Popconfirm, Skeleton, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { KeyRound, RefreshCw, ShieldCheck } from 'lucide-react'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { McpClientConfig } from './mcp-client-config.tsx'
import { getMcpEndpoint } from './utils/mcp-config.ts'

/** 明文密钥仅在当前弹窗内存中保留，关闭后卸载；Mutation 也不保留闲置缓存。 */
export function McpKeySettings() {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const [credential, setCredential] = useState<{ key: string; version: number }>()
  const status = useQuery({ ...trpc.mcpCredential.status.queryOptions(), retry: false, staleTime: 0 })
  const rotate = useMutation(
    trpc.mcpCredential.rotate.mutationOptions({
      gcTime: 0,
      onSuccess: async (result) => {
        const { key: newKey, ...metadata } = result
        setCredential({ key: newKey, version: result.version })
        // 状态缓存只保存元信息；即使后续刷新失败，新密钥也不会丢失。
        cache.setQueryData(trpc.mcpCredential.status.queryKey(), metadata)
        await cache.invalidateQueries(trpc.mcpCredential.status.queryFilter())
        void message.success('新密钥已生成，请复制并保存配置')
      }
    })
  )
  const revoke = useMutation(
    trpc.mcpCredential.revoke.mutationOptions({
      gcTime: 0,
      onSuccess: async () => {
        setCredential(undefined)
        rotate.reset()
        await cache.invalidateQueries(trpc.mcpCredential.status.queryFilter())
        void message.success('MCP 密钥已撤销')
      }
    })
  )
  const expired = !!status.data?.expiresAt && dayjs().isAfter(status.data.expiresAt)
  const active = !!status.data && !status.data.revokedAt && !expired
  const busy = rotate.isPending || revoke.isPending
  const disabled = busy || status.isFetching || status.isError
  const key = active && credential?.version === status.data?.version ? credential?.key : undefined
  const endpoint = getMcpEndpoint({
    origin: window.location.origin,
    development: import.meta.env.DEV,
    configuredUrl: import.meta.env.VITE_MCP_URL
  })

  const rotateKey = async () => {
    try {
      await rotate.mutateAsync()
    } catch (error) {
      void message.error(getErrorMessage(error, '密钥生成失败，请重试'))
    }
  }
  const revokeKey = async () => {
    try {
      await revoke.mutateAsync()
    } catch (error) {
      void message.error(getErrorMessage(error, '密钥撤销失败，请重试'))
    }
  }
  if (status.isPending) return <Skeleton active paragraph={{ rows: 4 }} />
  if (status.isError) {
    return (
      <Alert
        showIcon
        type="error"
        title="无法读取密钥状态"
        description={getErrorMessage(status.error, '请稍后重试')}
        action={
          <Button onClick={() => void status.refetch()} loading={status.isFetching}>
            重试
          </Button>
        }
      />
    )
  }

  return (
    <div className="flex flex-col gap-5 pt-4">
      <section aria-label="连接信息" className="border-border bg-surface-muted rounded-lg border px-4 py-3">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <span className="text-content-muted text-xs font-medium">服务地址</span>
          <span className="text-content-soft text-xs">Streamable HTTP</span>
        </div>
        <Typography.Text className="font-mono text-sm break-all" copyable={{ text: endpoint }}>
          {endpoint}
        </Typography.Text>
        <div className="border-border text-content-soft mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 border-t pt-2 text-xs">
          <span>认证方式</span>
          <span className="text-content-muted">Bearer Token</span>
          <span aria-hidden>·</span>
          <span>Authorization 请求头</span>
        </div>
      </section>

      <section
        aria-label="个人 MCP 密钥"
        className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-5"
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-content text-sm font-medium">个人密钥</span>
            <Tag color={active ? 'success' : 'default'}>{active ? '已启用' : '未启用'}</Tag>
            {status.data && <span className="text-content-muted font-mono text-xs">{status.data.prefix}…</span>}
          </div>
          <p className="text-content-muted m-0 mt-1.5 text-xs leading-5">
            {active && status.data
              ? `更新于 ${dayjs(status.data.updatedAt).format('YYYY-MM-DD HH:mm')}。重置后需更新客户端配置。`
              : '创建密钥，生成可直接使用的客户端配置。'}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap gap-2">
          {active ? (
            <Popconfirm
              title="重置个人 MCP 密钥？"
              description="旧密钥将立即失效，已连接的 AI 客户端需要更新配置。"
              onConfirm={rotateKey}
              okText="确认重置"
              cancelText="取消"
              disabled={disabled}
            >
              <Button icon={<RefreshCw size={16} />} loading={rotate.isPending} disabled={disabled}>
                重置密钥
              </Button>
            </Popconfirm>
          ) : (
            <Button
              type="primary"
              icon={<KeyRound size={16} />}
              onClick={() => void rotateKey()}
              loading={rotate.isPending}
              disabled={disabled}
            >
              创建密钥
            </Button>
          )}
          {active && (
            <Popconfirm
              title="撤销个人 MCP 访问？"
              description="当前密钥将无法继续访问 MCP 服务。"
              onConfirm={revokeKey}
              okText="确认撤销"
              cancelText="取消"
              disabled={disabled}
            >
              <Button danger type="text" loading={revoke.isPending} disabled={disabled}>
                撤销密钥
              </Button>
            </Popconfirm>
          )}
        </div>
      </section>

      <McpClientConfig endpoint={endpoint} secret={key} disabled={disabled} />
      <p className="text-content-soft m-0 flex items-start gap-2 text-xs leading-5">
        <ShieldCheck aria-hidden className="mt-0.5 size-4 shrink-0" />
        <span>AI 访问遵循当前账号权限。含密钥的配置仅用于可信客户端，请妥善保管。</span>
      </p>
    </div>
  )
}
